import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

/**
 * Controller for Plantafel task sections
 * Handles click events for "+" buttons to create inline tasks
 *
 * In 3-day and week views, each day is divided into 5 time zones matching
 * the visual grid dividers. The add buttons appear at the bottom of each section
 * and use CSS hover for visibility.
 */
export default class extends Controller {
  static targets = ["cellsLayer", "highlightLayer", "bottomTriggerLayer"]
  static values = {
    viewMode: String,
    workingHoursFrom: String,
    workingHoursTo: String,
    newLabel: String,
    existingLabel: String,
  }

  disconnect() {
    this.closeAddMenu()
  }

  /**
   * Highlights the full-height zone in the highlight layer
   * Called when hovering over zones (both empty zones in overlay and bottom trigger strip)
   */
  highlightZone(event) {
    if (!this.hasHighlightLayerTarget) return

    const zone = event.currentTarget
    const zoneIndex = zone.dataset.zone
    const cell = zone.closest(".plantafel-task-cell")
    const dateStr = cell?.dataset.date

    if (!dateStr) return

    // Find cell index by matching date
    const cellIndex = this.getCellIndexForDate(dateStr)
    if (cellIndex === -1) return

    // Find the corresponding highlight zone
    const highlightCells = this.highlightLayerTarget.children
    if (highlightCells[cellIndex]) {
      const targetHighlight = highlightCells[cellIndex].querySelector(
        `.zone-highlight[data-zone="${zoneIndex}"]`,
      )
      if (targetHighlight) {
        targetHighlight.style.backgroundColor = "rgba(20, 157, 113, 0.1)"
      }
    }
  }

  /**
   * Helper to find cell index by date
   */
  getCellIndexForDate(dateStr) {
    if (!this.hasCellsLayerTarget) return -1

    const cells = this.cellsLayerTarget.querySelectorAll(".plantafel-task-cell")
    return Array.from(cells).findIndex((cell) => cell.dataset.date === dateStr)
  }

  /**
   * Removes highlight from the full-height zone
   */
  unhighlightZone(event) {
    if (!this.hasHighlightLayerTarget) return

    const zone = event.currentTarget
    const zoneIndex = zone.dataset.zone
    const cell = zone.closest(".plantafel-task-cell")
    const dateStr = cell?.dataset.date

    if (!dateStr) return

    // Find cell index by matching date
    const cellIndex = this.getCellIndexForDate(dateStr)
    if (cellIndex === -1) return

    // Find the corresponding highlight zone
    const highlightCells = this.highlightLayerTarget.children
    if (highlightCells[cellIndex]) {
      const targetHighlight = highlightCells[cellIndex].querySelector(
        `.zone-highlight[data-zone="${zoneIndex}"]`,
      )
      if (targetHighlight) {
        targetHighlight.style.backgroundColor = ""
      }
    }
  }

  /**
   * Opens a small menu offering "create new task" or "schedule existing undated task".
   * When the zone has no existing-task option (non-site sections), falls back to opening
   * the new-task form directly so creating stays a single click.
   */
  openAddMenu(event) {
    event.preventDefault()
    event.stopPropagation()

    const zone = event.target.closest(".task-zone")
    const newPath = zone?.dataset.newPath
    const existingPath = zone?.dataset.existingPath

    if (!newPath) {
      Honeybadger.notify("Missing URL for add-task menu", {
        context: { level: "warning", component: "plantafel-task-section" },
      })
      return
    }

    if (!existingPath) {
      this.fetchForm(newPath)
      return
    }

    this.showAddMenu(event.currentTarget, newPath, existingPath)
  }

  showAddMenu(button, newPath, existingPath) {
    this.closeAddMenu()

    const menu = document.createElement("div")
    menu.className =
      "plantafel-add-menu fixed z-50 min-w-[210px] rounded-md bg-white shadow-lg border border-gray-200 py-1 text-sm"
    menu.appendChild(
      this.buildMenuItem(this.newLabelValue || "Neuen Task erstellen", "M12 4v16m8-8H4", () => {
        this.closeAddMenu()
        this.fetchForm(newPath)
      }),
    )
    menu.appendChild(
      this.buildMenuItem(
        this.existingLabelValue || "Bestehenden Task einplanen",
        "M4 6h16M4 12h16M4 18h7",
        () => {
          this.closeAddMenu()
          this.fetchForm(existingPath)
        },
      ),
    )

    document.body.appendChild(menu)
    this.addMenu = menu
    this.positionAddMenu(button, menu)

    this.boundCloseAddMenu = () => this.closeAddMenu()
    this.boundEscapeAddMenu = (event) => {
      if (event.key === "Escape") this.closeAddMenu()
    }
    setTimeout(() => {
      document.addEventListener("click", this.boundCloseAddMenu)
      document.addEventListener("keydown", this.boundEscapeAddMenu)
    }, 0)
  }

  positionAddMenu(button, menu) {
    const rect = button.getBoundingClientRect()
    const menuRect = menu.getBoundingClientRect()
    let top = rect.bottom + 4
    let left = rect.left
    if (left + menuRect.width > window.innerWidth - 8) {
      left = window.innerWidth - menuRect.width - 8
    }
    if (top + menuRect.height > window.innerHeight - 8) {
      top = rect.top - menuRect.height - 4
    }
    menu.style.top = `${Math.max(8, top)}px`
    menu.style.left = `${Math.max(8, left)}px`
  }

  buildMenuItem(label, iconPath, onClick) {
    const item = document.createElement("button")
    item.type = "button"
    item.className =
      "flex items-center gap-2 w-full px-3 py-2 text-left text-gray-700 hover:bg-green-50 transition-colors"
    item.innerHTML = `<svg class="w-4 h-4 text-bautask-green shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${iconPath}"></path></svg><span></span>`
    item.querySelector("span").textContent = label
    item.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      onClick()
    })
    return item
  }

  closeAddMenu() {
    if (this.addMenu) {
      this.addMenu.remove()
      this.addMenu = null
    }
    if (this.boundCloseAddMenu) {
      document.removeEventListener("click", this.boundCloseAddMenu)
      this.boundCloseAddMenu = null
    }
    if (this.boundEscapeAddMenu) {
      document.removeEventListener("keydown", this.boundEscapeAddMenu)
      this.boundEscapeAddMenu = null
    }
  }

  /**
   * Fetches the form via Turbo Stream and renders in sidedrawer
   */
  fetchForm(url) {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-task-section", action: "fetch_form" },
        })
        // Fallback to regular navigation
        window.location.href = url
      })
  }
}
